const HEALTH_CHECK_PATH = '/health.json';
export const API_HEALTH_CHECK_PATH = '/health';
const HEALTH_CHECK_TIMEOUT_MS = 2500;
const CACHE_TTL_MS = 10 * 60 * 1000;
const failures = [];
const logFailure = (url, reason) => {
    failures.push({ url, reason, timestamp: Date.now() });
    console.warn(`[endpoint-health] ${url} failed health check: ${reason}`);
};
export const getEndpointHealthFailures = () => failures;
export const checkEndpointHealth = async (baseUrl, timeoutMs = HEALTH_CHECK_TIMEOUT_MS, healthPath = HEALTH_CHECK_PATH, requireStatusOk = true) => {
    const healthUrl = `${baseUrl.replace(/\/$/, '')}${healthPath}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(healthUrl, { signal: controller.signal, cache: 'no-store' });
        if (!res.ok) {
            logFailure(healthUrl, `HTTP ${res.status}`);
            return false;
        }
        if (!requireStatusOk)
            return true;
        const data = await res.json();
        if (data?.status !== 'ok') {
            logFailure(healthUrl, `unexpected content: ${JSON.stringify(data)}`);
            return false;
        }
        return true;
    }
    catch (err) {
        logFailure(healthUrl, err instanceof Error ? err.message : String(err));
        return false;
    }
    finally {
        clearTimeout(timer);
    }
};
export const selectWorkingBase = async (primary, fallbacks = [], healthPath = HEALTH_CHECK_PATH, requireStatusOk = true) => {
    if (await checkEndpointHealth(primary, HEALTH_CHECK_TIMEOUT_MS, healthPath, requireStatusOk))
        return primary;
    for (const fallback of fallbacks) {
        if (await checkEndpointHealth(fallback, HEALTH_CHECK_TIMEOUT_MS, healthPath, requireStatusOk))
            return fallback;
    }
    return primary;
};
const readCache = (storageKey) => {
    if (typeof sessionStorage === 'undefined')
        return null;
    try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.timestamp > CACHE_TTL_MS)
            return null;
        return parsed;
    }
    catch {
        return null;
    }
};
const writeCache = (storageKey, primary, resolved) => {
    if (typeof sessionStorage === 'undefined')
        return;
    try {
        const entry = { primary, resolved, timestamp: Date.now() };
        sessionStorage.setItem(storageKey, JSON.stringify(entry));
    }
    catch {
        // sessionStorage may be unavailable; skipping cache is not fatal
    }
};
export const selectWorkingBaseCached = async (storageKey, primary, fallbacks = [], healthPath = HEALTH_CHECK_PATH, requireStatusOk = true) => {
    const cached = readCache(storageKey);
    if (cached && cached.primary === primary)
        return cached.resolved;
    const resolved = await selectWorkingBase(primary, fallbacks, healthPath, requireStatusOk);
    writeCache(storageKey, primary, resolved);
    return resolved;
};
export const readCachedBase = (storageKey, primary) => {
    const cached = readCache(storageKey);
    return cached && cached.primary === primary ? cached.resolved : null;
};
/**
 * True when a background check swapped the primary for a mirror that the next boot will read
 * from the cache. Requiring the cache hit is what keeps a storage-less browser from reload-looping.
 */
export const shouldReloadForMirror = (storageKey, primary, resolved) => resolved !== primary && readCachedBase(storageKey, primary) === resolved;
